import { Text } from '@chakra-ui/react';

import { UserProfile } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import OBDocumentView from 'features/Bento/components/OBDocumentView';
import DocumentChecklist from 'features/Onboarding/components/DocumentChecklist';
import Header from 'features/Onboarding/components/Header';

import { AddressSchema } from '../../../../components/AddressForm/addressSchema';
import FormattedAddress from '../../components/FormattedAddress';
import { getFormattedDateThreeMonthsAgo } from '../../libs/locales';
import { PoaType } from '../../machine/model';
import locales from './locales';

interface ProofOfAddressChecklistProps {
  isSelfHosted: boolean;
  isSaving: boolean;
  documentFileUrl: string;
  profile: Pick<UserProfile, 'firstName' | 'lastName'>;
  address: AddressSchema;
  poaType: PoaType;
  onContinue: () => void;
}

/**
 * A page asking the user to check that their document is admissible as a proof of address.
 */
const ProofOfAddressChecklist = asBentoPage<ProofOfAddressChecklistProps>(
  ({
    address,
    documentFileUrl,
    isSaving,
    isSelfHosted,
    onContinue,
    onPrev,
    poaType,
    profile,
  }) => {
    const name =
      profile.firstName && profile.lastName
        ? `${locales.firstNameLabel} ${profile.firstName}\n${locales.lastNameLabel} ${profile.lastName}`
        : null;

    const lightOnSave = isSaving ? 'light' : undefined;

    let checks = [
      {
        key: 'valid',
        label: locales.valid,
      },
      {
        key: 'date',
        label: locales.formatString(locales.checkDate, {
          formattedDateThreeMonthsAgo: getFormattedDateThreeMonthsAgo(),
        }) as string,
      },
      isSelfHosted
        ? {
            description: name && <Text variant={lightOnSave}>{name}</Text>,
            key: 'name',
            label: name ? locales.checkName : locales.checkOwnName,
          }
        : {
            key: 'name',
            label: locales.checkHostName,
          },
      {
        description: (
          <FormattedAddress address={address} as="span" variant={lightOnSave} />
        ),
        key: 'address',
        label: locales.checkAddress,
      },
      {
        key: 'quality',
        label: locales.checkQuality,
      },
    ];

    if (poaType === 'LANDLINE') {
      checks.unshift({
        key: 'landline',
        label: locales.checkLandline,
      });
    }

    // if the document is an income tax notice, it can be older than 3 months
    if (poaType === 'INCOME_TAX_NOTICE') {
      checks = checks.filter((check) => {
        return check.key !== 'date';
      });
    }

    return (
      <OBDocumentView
        data-testid="proof-of-address-checklist"
        onClose={onPrev}
        src={documentFileUrl}
      >
        <Header title={locales.title} />

        <Text marginBottom="space-32" variant="primary">
          {locales.description}
        </Text>

        <DocumentChecklist
          checks={checks}
          isLoading={isSaving}
          onSubmit={onContinue}
        />
      </OBDocumentView>
    );
  },
);
export default ProofOfAddressChecklist;
